import React, { useEffect, useMemo, useState } from 'react';
import Api from '../../../Api';
import SearchInput from '../../../components/SearchInput';
import MyCheckBox from '../../../components/MyCheckbox';
import MyButton from '../../../components/MyButton';
import MapView from '../../AddOrganization/components/MapView';
import CarouselPhotosField from '../../../components/CarouselPhotosField';
import {
  ALL_CITIES_OPTION_ID,
  buildAllCitiesSelection,
  isAllCitiesSelection,
} from '../../../helpers/allCitiesOfCountry';
import { fetchCitiesOfCountryCached } from '../../../helpers/eventsBulkAutofill';
import { FIELD_TYPES } from '../constants';

function useCitiesOfCountry(countryId) {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!countryId) {
      setCities([]);
      return undefined;
    }
    setLoading(true);
    fetchCitiesOfCountryCached(countryId)
      .then((list) => { if (!cancelled) setCities(list); })
      .catch(() => { if (!cancelled) setCities([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [countryId]);

  return { cities, loading };
}

function FieldSelect({
  value, onChange, options, placeholder = '— Не выбрано —', disabled,
}) {
  return (
    <select
      className="bulk_offers_select"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      <option value="">{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  );
}

function HoldingDatesEditor({ value, onChange }) {
  const dates = value?.length ? value : [''];

  const updateAt = (index, next) => {
    onChange(dates.map((item, i) => (i === index ? next : item)));
  };

  return (
    <div className="bulk_offers_field_rows">
      {dates.map((dateValue, index) => (
        <div className="bulk_offers_field_inline" key={`holding-${index}`}>
          <SearchInput
            wrapperClassName="bulk_offers_input"
            placeholder={`День ${index + 1}`}
            withLeftIcon={false}
            withCloseIcon={false}
            value={dateValue}
            onChange={(e) => updateAt(index, e?.target?.value || '')}
            shouldValueConsiderAsFocus={false}
            type="date"
          />
          {dates.length > 1 ? (
            <MyButton
              text="Удалить"
              secondary
              className="bulk_offers_toolbar_button"
              onClick={() => onChange(dates.filter((_, i) => i !== index))}
            />
          ) : null}
        </div>
      ))}
      <MyButton
        text="Добавить день"
        secondary
        className="bulk_offers_toolbar_button"
        onClick={() => onChange([...dates, ''])}
      />
    </div>
  );
}

function CityEditor({
  value, onChange, countries, lockedCountryId,
}) {
  const [countryId, setCountryId] = useState(value?.country_id || lockedCountryId || '');
  const { cities, loading } = useCitiesOfCountry(countryId);
  const country = useMemo(
    () => (countries || []).find((item) => item._id === countryId) || null,
    [countries, countryId],
  );
  const selectedCityValue = isAllCitiesSelection(value) ? ALL_CITIES_OPTION_ID : value?._id;

  const handleCityChange = (cityId) => {
    if (!cityId) {
      onChange(null);
      return;
    }
    if (cityId === ALL_CITIES_OPTION_ID) {
      onChange(buildAllCitiesSelection(country));
      return;
    }
    const city = cities.find((item) => item._id === cityId);
    onChange(city ? { ...city, is_in_all_cities_of_country: false } : null);
  };

  return (
    <div className="bulk_offers_field_rows">
      <FieldSelect
        value={countryId}
        options={(countries || []).map((item) => ({ value: item._id, label: item.name }))}
        onChange={(id) => {
          setCountryId(id);
          onChange(null);
        }}
        placeholder="— Выберите страну —"
        disabled={Boolean(lockedCountryId)}
      />
      <FieldSelect
        value={selectedCityValue}
        options={[
          { value: ALL_CITIES_OPTION_ID, label: 'Все города страны' },
          ...cities.map((city) => ({ value: city._id, label: city.name })),
        ]}
        onChange={handleCityChange}
        placeholder={loading ? 'Загрузка городов…' : '— Выберите город —'}
        disabled={!countryId || loading}
      />
    </div>
  );
}

function CoordinatesEditor({ value, onChange, city }) {
  const handlePart = (part) => (e) => {
    const nextValue = typeof e === 'string' ? e : e.target.value;
    onChange({
      lat: value?.lat || '',
      lon: value?.lon || '',
      is_special_point_on_map: Boolean(value?.is_special_point_on_map),
      [part]: nextValue,
    });
  };

  return (
    <div className="bulk_offers_field_rows">
      <div className="bulk_offers_field_inline">
        <SearchInput
          wrapperClassName="bulk_offers_input"
          withLeftIcon={false}
          withCloseIcon={false}
          placeholder="Широта (lat)"
          value={value?.lat || ''}
          onChange={handlePart('lat')}
          shouldValueConsiderAsFocus={false}
        />
        <SearchInput
          wrapperClassName="bulk_offers_input"
          withLeftIcon={false}
          withCloseIcon={false}
          placeholder="Долгота (lon)"
          value={value?.lon || ''}
          onChange={handlePart('lon')}
          shouldValueConsiderAsFocus={false}
        />
      </div>
      <MapView
        city={city}
        coordinates={value}
        setCoordinates={onChange}
        onMapPress={() => {}}
      />
    </div>
  );
}

function EventFieldEditor({
  field,
  value,
  onChange,
  draft = null,
  categories = [],
  countries = [],
  languages = [],
  config = {},
}) {
  if (!field) return null;

  switch (field.type) {
    case FIELD_TYPES.text:
    case FIELD_TYPES.contact:
      return (
        <SearchInput
          wrapperClassName={`bulk_offers_input${field.digitsOnly ? ' bulk_offers_input_with_hint' : ''}`}
          withLeftIcon={false}
          placeholder={field.label}
          value={value || ''}
          onChange={(e) => onChange(typeof e === 'string' ? e : e.target.value)}
          shouldValueConsiderAsFocus={false}
          labelText={field.digitsOnly ? 'только цифры' : undefined}
        />
      );

    case FIELD_TYPES.date:
      return (
        <SearchInput
          wrapperClassName="bulk_offers_input"
          withLeftIcon={false}
          withCloseIcon={false}
          placeholder={field.label}
          value={value || ''}
          onChange={(e) => onChange(e?.target?.value || '')}
          shouldValueConsiderAsFocus={false}
          type="date"
        />
      );

    case FIELD_TYPES.textarea:
      return (
        <SearchInput
          wrapperClassName="bulk_offers_input bulk_offers_input_large"
          withLeftIcon={false}
          placeholder={field.label}
          value={value || ''}
          onChange={(e) => onChange(typeof e === 'string' ? e : e.target.value)}
          isLarge
          shouldValueConsiderAsFocus={false}
        />
      );

    case FIELD_TYPES.holdingDates:
      return <HoldingDatesEditor value={value} onChange={onChange} />;

    case FIELD_TYPES.category:
      return (
        <FieldSelect
          value={value?._id}
          options={(categories || []).map((item) => ({ value: item._id, label: item.name }))}
          onChange={(id) => onChange((categories || []).find((item) => item._id === id) || null)}
          placeholder="— Выберите категорию —"
        />
      );

    case FIELD_TYPES.city:
      return (
        <CityEditor
          value={value}
          onChange={onChange}
          countries={countries}
          lockedCountryId={config?.country?._id || ''}
        />
      );

    case FIELD_TYPES.address:
      return (
        <SearchInput
          wrapperClassName="bulk_offers_input"
          withLeftIcon={false}
          placeholder="Адрес"
          value={value || ''}
          onChange={(e) => onChange(typeof e === 'string' ? e : e.target.value)}
          shouldValueConsiderAsFocus={false}
        />
      );

    case FIELD_TYPES.coordinates:
      return (
        <CoordinatesEditor
          value={value}
          onChange={onChange}
          city={draft?.city || null}
        />
      );

    case FIELD_TYPES.isForAllLanguages:
      return (
        <MyCheckBox
          value={Boolean(value)}
          setValue={(next) => onChange(typeof next === 'function' ? next(Boolean(value)) : next)}
          text="Мероприятие подходит для любых языков"
        />
      );

    case FIELD_TYPES.languages: {
      const selected = value || [];
      const selectedIds = selected.map((item) => item._id);
      if (!languages?.length) return <p className="bulk_offers_field_note">Справочник языков не загружен.</p>;
      return (
        <div className="bulk_offers_checkbox_list">
          {languages.map((item) => (
            <MyCheckBox
              key={item._id}
              value={selectedIds.includes(item._id)}
              onClick={() => {
                if (selectedIds.includes(item._id)) {
                  onChange(selected.filter((lang) => lang._id !== item._id));
                } else {
                  onChange([...selected, item]);
                }
              }}
              text={item.name}
              imgUrl={item.flag_url ? Api.url + item.flag_url : undefined}
              wrapperClassName="bulk_offers_checkbox_item"
            />
          ))}
        </div>
      );
    }

    case FIELD_TYPES.photos:
      return <CarouselPhotosField value={value || []} onChange={onChange} max={15} />;

    default:
      return null;
  }
}

export default EventFieldEditor;
